/**
 * SettingsStore — single source of truth for user-configurable client settings.
 *
 * Pure persistence + change notification. Holds NO engine references, so it
 * can be imported anywhere (UI screen, boot code) without risking an import
 * cycle. `main.ts` owns the apply side: it subscribes to `change` and pushes
 * values into the live subsystems (AudioManager volumes/mute, SharedRenderer
 * quality). The SettingsScreen only reads/writes this store.
 *
 * Storage: one JSON blob under `sdl_settings`. Versioned so we can migrate the
 * shape later without blowing away a beta tester's prefs.
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import type { RenderQuality } from '../engine/render/SharedRenderer';

const STORAGE_KEY = 'sdl_settings';
const SCHEMA_VERSION = 1;

export interface AudioSettings {
  /** 0..1 master multiplier applied on top of every category. */
  master: number;
  music: number;
  /** In-game sound effects (dice, wins, table). */
  sfx: number;
  /** App / menu UI effects (taps, transitions). */
  ui: number;
  /** Incoming voice-chat playback level. */
  voice: number;
  /** Hard mute — overrides all category volumes. */
  muted: boolean;
  /** Whether the local mic is broadcast to the table (track-1 voice chat). */
  voiceChatEnabled: boolean;
}

export interface GraphicsSettings {
  quality: RenderQuality;
}

export interface ChatSettings {
  /** Floating chat bubbles over player heads in lobby / game (track-1). */
  bubblesEnabled: boolean;
}

export interface NotificationSettings {
  /**
   * Player's stated preference for push notifications. This is the in-app
   * "mute" switch — it gates whether we keep a device token registered with the
   * server, NOT the OS permission (which we can't toggle from here). On +
   * permission granted → registered. Off → token unregistered, server stops
   * pushing. Defaults true so a player who grants the OS prompt gets pushes.
   */
  enabled: boolean;
}

export interface SettingsState {
  audio: AudioSettings;
  graphics: GraphicsSettings;
  chat: ChatSettings;
  notifications: NotificationSettings;
}

const DEFAULTS: SettingsState = {
  audio: {
    master: 1,
    music: 0.6,
    sfx: 1,
    ui: 0.8,
    voice: 1,
    muted: false,
    voiceChatEnabled: true,
  },
  graphics: {
    quality: 'high',
  },
  chat: {
    bubblesEnabled: true,
  },
  notifications: {
    enabled: true,
  },
};

interface PersistedShape {
  version: number;
  state: SettingsState;
}

/** Deep-merge persisted values over defaults so new keys get sane fallbacks. */
function hydrate(raw: string | null): SettingsState {
  if (!raw) return structuredClone(DEFAULTS);
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    const saved: Partial<SettingsState> = parsed.state ?? {};
    return {
      audio: { ...DEFAULTS.audio, ...(saved.audio ?? {}) },
      graphics: { ...DEFAULTS.graphics, ...(saved.graphics ?? {}) },
      chat: { ...DEFAULTS.chat, ...(saved.chat ?? {}) },
      notifications: { ...DEFAULTS.notifications, ...(saved.notifications ?? {}) },
    };
  } catch {
    return structuredClone(DEFAULTS);
  }
}

export type SettingsSection = keyof SettingsState;

export interface SettingsStoreEvents {
  change: SettingsState;
}

class SettingsStoreImpl extends EventEmitter<SettingsStoreEvents> {
  private state: SettingsState;

  constructor() {
    super();
    this.state = hydrate(localStorage.getItem(STORAGE_KEY));
  }

  /** Snapshot of all settings (cloned — callers can't mutate internal state). */
  getAll(): SettingsState {
    return structuredClone(this.state);
  }

  get<S extends SettingsSection>(section: S): SettingsState[S] {
    return structuredClone(this.state[section]);
  }

  /**
   * Patch one section, persist, and emit `change`. Partial patch so a slider
   * can write a single field without restating the whole section.
   */
  update<S extends SettingsSection>(section: S, patch: Partial<SettingsState[S]>): void {
    this.state[section] = { ...this.state[section], ...patch };
    this.persist();
    this.emit('change', this.getAll());
  }

  reset(): void {
    this.state = structuredClone(DEFAULTS);
    this.persist();
    this.emit('change', this.getAll());
  }

  private persist(): void {
    try {
      const blob: PersistedShape = { version: SCHEMA_VERSION, state: this.state };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
    } catch (err) {
      // Storage can throw in private-mode / quota cases — non-fatal, settings
      // just won't survive a reload.
      console.warn('[SettingsStore] persist failed:', err);
    }
  }
}

/** App-wide singleton. */
export const settingsStore = new SettingsStoreImpl();
